import React, { useEffect, useMemo, useState } from 'react';
import AutoGraphRoundedIcon from '@mui/icons-material/AutoGraphRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PrecisionManufacturingRoundedIcon from '@mui/icons-material/PrecisionManufacturingRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { fetchAuthenticatedJson } from '../../api/authenticatedFetch';

const jobs = [
  {
    name: 'UniverseBuilderJob',
    label: 'Universe Builder',
    api: '/api/admin/job/universe-builder',
    description: 'Reconstruit l univers de travail et les actifs suivis par le cockpit.',
    icon: <PrecisionManufacturingRoundedIcon />,
  },
  {
    name: 'AssetScannerJob',
    label: 'Asset Scanner',
    api: '/api/admin/job/asset-scanner',
    description: 'Relance le scan principal pour recalculer les opportunites et signaux.',
    icon: <AutoGraphRoundedIcon />,
  },
  {
    name: 'PatternScannerJob',
    label: 'Pattern Scanner',
    api: '/api/admin/job/pattern-scanner',
    description: 'Declenche l analyse des patterns techniques sur l univers courant.',
    icon: <TimelineRoundedIcon />,
  },
  {
    name: 'CryptoPriceHistoryJob',
    label: 'Historique des prix crypto',
    api: '/api/admin/job/crypto-price-history',
    description: 'Met a jour l historique de prix utile aux calculs et vues retroactives.',
    icon: <SavingsRoundedIcon />,
  },
  {
    name: 'SimulatePriceHistory',
    label: 'Simuler 60 jours de prix',
    api: '/api/admin/job/simulate-price-history',
    description: 'Genere un jeu de donnees de prix pour enrichir un environnement de dev.',
    icon: <PlayArrowRoundedIcon />,
  },
];

function StatBadge({ label, value, accent = '#19323c' }) {
  return (
    <Box
      sx={{
        px: 1.5,
        py: 1.25,
        borderRadius: 3,
        background: 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(247,243,234,0.12)',
        minWidth: 132,
      }}
    >
      <Typography variant="caption" sx={{ color: 'rgba(247,243,234,0.72)', display: 'block', mb: 0.35 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: accent, fontWeight: 800 }}>
        {value}
      </Typography>
    </Box>
  );
}

function formatDateTime(value) {
  if (!value) {
    return 'n/a';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString('fr-FR');
}

function formatDuration(value) {
  if (value == null) {
    return 'n/a';
  }

  return `${Math.round(value * 10) / 10} ms`;
}

function formatPercent(value) {
  if (value == null) {
    return 'n/a';
  }

  return `${Math.round(value * 10) / 10}%`;
}

function formatBytes(value) {
  if (value == null) {
    return 'n/a';
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let current = Math.abs(value);
  let index = 0;

  while (current >= 1024 && index < units.length - 1) {
    current /= 1024;
    index += 1;
  }

  const signed = value < 0 ? -current : current;
  return `${Math.round(signed * 10) / 10} ${units[index]}`;
}

function compactValue(value) {
  return value == null || value === '' ? 'n/a' : `${value}`;
}

const JobAdminPage = () => {
  const [loadingJob, setLoadingJob] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [lastRun, setLastRun] = useState(null);
  const [diagnosticLoading, setDiagnosticLoading] = useState(true);
  const [diagnosticError, setDiagnosticError] = useState('');
  const [diagnostic, setDiagnostic] = useState(null);

  const idle = useMemo(() => !loadingJob, [loadingJob]);
  const recentEvents = useMemo(() => (diagnostic?.events || []).slice(-8).reverse(), [diagnostic]);

  const loadDiagnostic = async (showSpinner = true) => {
    if (showSpinner) {
      setDiagnosticLoading(true);
    }
    setDiagnosticError('');

    try {
      const payload = await fetchAuthenticatedJson('/api/admin/job/pattern-scanner-diagnostic');
      setDiagnostic(payload);
    } catch (e) {
      setDiagnosticError(e.message);
    } finally {
      setDiagnosticLoading(false);
    }
  };

  useEffect(() => {
    loadDiagnostic();
  }, []);

  const handleRunJob = async (api) => {
    const currentJob = jobs.find((job) => job.api === api);
    setLoadingJob(currentJob?.name || api);
    setError('');
    setSuccessMessage('');
    try {
      const payload = await fetchAuthenticatedJson(api, { method: 'POST' });
      if (payload?.status === 'FAILED') {
        throw new Error(payload?.message || 'Erreur lors du déclenchement du job');
      }
      const message = payload?.message || 'Job declenche avec succes';
      setSuccessMessage(message);
      setLastRun({
        label: currentJob?.label || 'Job admin',
        message,
        at: new Date().toLocaleString('fr-FR'),
      });

      if (currentJob?.name === 'PatternScannerJob') {
        await loadDiagnostic(false);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingJob('');
    }
  };

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(180deg, #f4efe6 0%, #eef3f6 100%)', minHeight: '100%' }}>
      <Stack spacing={3}>
        <Paper sx={{ p: 3.5, borderRadius: 5, background: 'linear-gradient(135deg, #17323c 0%, #355b63 100%)', color: '#f7f3ea' }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} justifyContent="space-between">
            <Box sx={{ maxWidth: 760 }}>
              <Chip label="Admin Jobs" sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700, mb: 1.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.25 }}>
                Declencher les traitements manuels de l'administration
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(247,243,234,0.82)', lineHeight: 1.7 }}>
                Cette page permet de lancer les jobs backend a la demande pour reconstruire l'univers, rescanner les actifs, recalculer les patterns ou remplir l'historique de prix.
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap" sx={{ alignSelf: 'flex-start' }}>
              <StatBadge label="Jobs disponibles" value={`${jobs.length}`} accent="#f7f3ea" />
              <StatBadge label="Etat" value={idle ? 'IDLE' : 'RUNNING'} accent={idle ? '#8fd6b1' : '#f3c65c'} />
              <StatBadge label="Dernier lancement" value={lastRun?.label || 'Aucun'} accent={lastRun ? '#f3c65c' : '#f7f3ea'} />
            </Stack>
          </Stack>
        </Paper>

        <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3} alignItems="stretch">
          <Paper sx={{ flex: 1.15, p: 3, borderRadius: 5, background: '#fffaf2' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#19323c', mb: 0.75 }}>
              Actions disponibles
            </Typography>
            <Typography variant="body2" sx={{ color: '#4f646f', mb: 3 }}>
              Chaque bouton declenche directement l'endpoint admin correspondant. L'execution backend continue ensuite selon la logique du job appele.
            </Typography>

            <Stack spacing={1.8}>
              {jobs.map((job) => {
                const isRunning = loadingJob === job.name;
                return (
                  <Paper
                    key={job.name}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 4,
                      background: '#fff',
                      border: '1px solid rgba(18,44,52,0.08)',
                    }}
                  >
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 3,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#f6f1e5',
                            color: '#17323c',
                            flexShrink: 0,
                          }}
                        >
                          {job.icon}
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#19323c', mb: 0.35 }}>
                            {job.label}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#4f646f' }}>
                            {job.description}
                          </Typography>
                        </Box>
                      </Stack>

                      <Button
                        variant={isRunning ? 'outlined' : 'contained'}
                        onClick={() => handleRunJob(job.api)}
                        disabled={!idle}
                        startIcon={isRunning ? <CircularProgress size={18} color="inherit" /> : <PlayArrowRoundedIcon />}
                        sx={{
                          minHeight: 48,
                          minWidth: 180,
                          borderRadius: 99,
                          bgcolor: isRunning ? 'transparent' : '#17323c',
                          color: isRunning ? '#17323c' : '#f7f3ea',
                          borderColor: '#17323c',
                          ':hover': isRunning ? { borderColor: '#17323c' } : { bgcolor: '#0f2329' },
                        }}
                      >
                        {isRunning ? 'Execution...' : `Lancer ${job.label}`}
                      </Button>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Paper>

          <Paper sx={{ flex: 0.92, p: 3, borderRadius: 5, background: '#fff' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#19323c', mb: 0.75 }}>
              Etat et retour
            </Typography>
            <Typography variant="body2" sx={{ color: '#4f646f', mb: 3 }}>
              Les retours de l'API admin sont affiches ici pour eviter les popups navigateur et garder une lecture coherente avec le reste du back-office.
            </Typography>

            <Stack spacing={2}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 4, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)' }}>
                <Typography variant="subtitle2" sx={{ color: '#19323c', fontWeight: 700, mb: 0.8 }}>
                  Regles d'execution
                </Typography>
                <Typography variant="body2" sx={{ color: '#4f646f' }}>
                  Un seul declenchement est permis a la fois depuis cette page. Lorsque le backend confirme l'appel, le message de succes ou d'erreur reste visible ici jusqu'au prochain lancement.
                </Typography>
              </Paper>

              {error && <Alert severity="error">{error}</Alert>}
              {successMessage && <Alert severity="success">{successMessage}</Alert>}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.4} useFlexGap flexWrap="wrap">
                <Paper elevation={0} sx={{ px: 1.6, py: 1.4, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)', minWidth: 132 }}>
                  <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
                    Job courant
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 800 }}>
                    {loadingJob ? jobs.find((job) => job.name === loadingJob)?.label || loadingJob : 'Aucun'}
                  </Typography>
                </Paper>
                <Paper elevation={0} sx={{ px: 1.6, py: 1.4, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)', minWidth: 132 }}>
                  <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
                    Derniere action
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 800 }}>
                    {lastRun?.label || 'Aucune'}
                  </Typography>
                </Paper>
                <Paper elevation={0} sx={{ px: 1.6, py: 1.4, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)', minWidth: 132 }}>
                  <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
                    Horodatage
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 800 }}>
                    {lastRun?.at || 'n/a'}
                  </Typography>
                </Paper>
              </Stack>

              <Paper elevation={0} sx={{ p: 2, borderRadius: 4, background: '#f8fbff', border: '1px solid rgba(18,44,52,0.08)' }}>
                <Typography variant="subtitle2" sx={{ color: '#19323c', fontWeight: 700, mb: 0.8 }}>
                  Dernier message
                </Typography>
                <Typography variant="body2" sx={{ color: '#4f646f', lineHeight: 1.7 }}>
                  {lastRun?.message || 'Aucun job n a encore ete declenche depuis cette page.'}
                </Typography>
              </Paper>
            </Stack>
          </Paper>
        </Stack>

        <Paper sx={{ p: 3, borderRadius: 5, background: '#fff', border: '1px solid rgba(18,44,52,0.08)' }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', lg: 'center' }} sx={{ mb: 2.5 }}>
            <Box sx={{ maxWidth: 820 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#19323c', mb: 0.75 }}>
                Diagnostic multi-engine
              </Typography>
              <Typography variant="body2" sx={{ color: '#4f646f', lineHeight: 1.7 }}>
                Cette vue lit directement le dernier snapshot backend du Pattern Scanner pour distinguer dispatch, execution, qualification, persistence et filtrage par engine.
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap">
              <StatBadge label="Statut" value={diagnostic?.status || 'IDLE'} accent={diagnostic?.status === 'FAILED' ? '#d96c6c' : diagnostic?.status === 'SUCCESS' ? '#8fd6b1' : '#19323c'} />
              <StatBadge label="Execution" value={diagnostic?.executionModel || 'n/a'} accent="#f7f3ea" />
              <StatBadge label="Active engines" value={diagnostic?.activeEngines?.join(', ') || 'n/a'} accent="#f3c65c" />
              <Button
                variant="outlined"
                onClick={() => loadDiagnostic()}
                disabled={diagnosticLoading || !idle}
                startIcon={diagnosticLoading ? <CircularProgress size={18} color="inherit" /> : <TimelineRoundedIcon />}
                sx={{ minHeight: 48, borderRadius: 99, borderColor: '#17323c', color: '#17323c' }}
              >
                {diagnosticLoading ? 'Lecture...' : 'Rafraichir diagnostic'}
              </Button>
            </Stack>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.4} useFlexGap flexWrap="wrap" sx={{ mb: 2.5 }}>
            <Paper elevation={0} sx={{ px: 1.6, py: 1.4, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)', minWidth: 168 }}>
              <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
                Scan ID
              </Typography>
              <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 800 }}>
                {diagnostic?.scanId || 'Aucun'}
              </Typography>
            </Paper>
            <Paper elevation={0} sx={{ px: 1.6, py: 1.4, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)', minWidth: 168 }}>
              <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
                Trigger
              </Typography>
              <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 800 }}>
                {diagnostic?.trigger || 'n/a'}
              </Typography>
            </Paper>
            <Paper elevation={0} sx={{ px: 1.6, py: 1.4, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)', minWidth: 168 }}>
              <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
                Debut
              </Typography>
              <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 800 }}>
                {formatDateTime(diagnostic?.startedAt)}
              </Typography>
            </Paper>
            <Paper elevation={0} sx={{ px: 1.6, py: 1.4, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)', minWidth: 168 }}>
              <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
                Fin
              </Typography>
              <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 800 }}>
                {formatDateTime(diagnostic?.finishedAt)}
              </Typography>
            </Paper>
            <Paper elevation={0} sx={{ px: 1.6, py: 1.4, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)', minWidth: 168 }}>
              <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
                Analyses rafraichies
              </Typography>
              <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 800 }}>
                {compactValue(diagnostic?.refreshedAnalyses)}
              </Typography>
            </Paper>
            <Paper elevation={0} sx={{ px: 1.6, py: 1.4, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)', minWidth: 168 }}>
              <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
                Detection persistence
              </Typography>
              <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 800 }}>
                {diagnostic?.detectionPersistenceModel || 'n/a'}
              </Typography>
            </Paper>
          </Stack>

          {diagnosticError && <Alert severity="error" sx={{ mb: 2 }}>{diagnosticError}</Alert>}

          {!diagnosticLoading && !diagnostic?.scanId && !diagnosticError && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Aucun cycle de Pattern Scanner n'a encore ete observe par le diagnostic.
            </Alert>
          )}

          <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2.5} alignItems="stretch">
            <Box sx={{ flex: 1.3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#19323c', mb: 1.2 }}>
                Vue par engine
              </Typography>
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.6} useFlexGap flexWrap="wrap">
                {(diagnostic?.engines || []).map((engine) => (
                  <Paper
                    key={engine.engineVersion}
                    elevation={0}
                    sx={{
                      flex: '1 1 320px',
                      p: 2,
                      borderRadius: 4,
                      background: '#fffaf2',
                      border: '1px solid rgba(18,44,52,0.08)',
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.2} sx={{ mb: 1.3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: '#19323c' }}>
                        {engine.engineVersion}
                      </Typography>
                      <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                        <Chip size="small" label={engine.configuredActive ? 'ACTIVE' : 'OBSERVED'} sx={{ bgcolor: engine.configuredActive ? '#e9f7ef' : '#eef3f6', color: '#19323c', fontWeight: 700 }} />
                        {engine.failed > 0 && <Chip size="small" label={`FAILED ${engine.failed}`} sx={{ bgcolor: '#fcebea', color: '#9b2c2c', fontWeight: 700 }} />}
                      </Stack>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap" sx={{ mb: 1.4 }}>
                      <StatBadge label="Dispatch/Start" value={`${engine.dispatched} / ${engine.started}`} />
                      <StatBadge label="Complete/Fail" value={`${engine.completed} / ${engine.failed}`} />
                      <StatBadge label="Detected/Zero" value={`${engine.detected} / ${engine.detectedZero}`} />
                      <StatBadge label="Qualified/Filtered" value={`${engine.qualified} / ${engine.filtered}`} />
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap" sx={{ mb: 1.4 }}>
                      <StatBadge label="Analyses persisted" value={compactValue(engine.analysesPersisted)} accent="#17323c" />
                      <StatBadge label="Detections persisted" value={compactValue(engine.detectionsPersisted)} accent="#17323c" />
                      <StatBadge label="Avg total" value={formatDuration(engine.averageTotalDurationMs)} accent="#17323c" />
                      <StatBadge label="Avg persistence" value={formatDuration(engine.averagePersistenceDurationMs)} accent="#17323c" />
                    </Stack>

                    <Stack spacing={0.8}>
                      <Typography variant="body2" sx={{ color: '#4f646f' }}>
                        Detection {formatDuration(engine.averageDetectionDurationMs)} | Qualification {formatDuration(engine.averageQualificationDurationMs)} | Max total {formatDuration(engine.maxTotalDurationMs)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#4f646f' }}>
                        CPU process {formatPercent(engine.averageProcessCpuPercent)} | CPU system {formatPercent(engine.averageSystemCpuPercent)} | Memory delta {formatBytes(engine.averageMemoryDeltaBytes)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#4f646f' }}>
                        Peak memory {formatBytes(engine.peakUsedMemoryBytes)} | DB active {compactValue(engine.peakDbActiveConnections)} | DB total {compactValue(engine.peakDbTotalConnections)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#4f646f' }}>
                        Missing levels {compactValue(engine.missingRequiredLevels)} | No closed candle {compactValue(engine.noClosedCandle)} | Stale {compactValue(engine.stale)} | Duplicates {compactValue(engine.duplicates)}
                      </Typography>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Box>

            <Paper sx={{ flex: 0.88, p: 2, borderRadius: 4, background: '#f8fbff', border: '1px solid rgba(18,44,52,0.08)' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#19323c', mb: 1.2 }}>
                Derniers evenements
              </Typography>
              <Stack spacing={1.1}>
                {recentEvents.length === 0 && (
                  <Typography variant="body2" sx={{ color: '#4f646f' }}>
                    Aucun evenement detaille a afficher pour le moment.
                  </Typography>
                )}
                {recentEvents.map((event, index) => (
                  <Paper key={`${event.scanId}-${event.timestamp}-${event.stage}-${index}`} elevation={0} sx={{ p: 1.4, borderRadius: 3, background: '#fff', border: '1px solid rgba(18,44,52,0.08)' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 0.6 }}>
                      <Stack direction="row" spacing={0.8} alignItems="center" useFlexGap flexWrap="wrap">
                        <Chip size="small" label={event.stage} sx={{ bgcolor: '#eef3f6', color: '#19323c', fontWeight: 700 }} />
                        <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 700 }}>
                          {event.engineVersion}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#4f646f' }}>
                          {event.symbol} {event.exchange} {event.timeframe}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" sx={{ color: '#6c7a80' }}>
                        {formatDateTime(event.timestamp)}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ color: '#4f646f' }}>
                      {event.detail || 'Aucun detail complementaire'}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
};

export default JobAdminPage;
